import { forwardRef, useId, useState } from 'react'
import type { InputHTMLAttributes } from 'react'
import styles from './Champ.module.css'

interface ProprietesChamp extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'id'> {
  readonly libelle: string
  /** Texte d'aide affiché sous le champ, avant toute erreur. */
  readonly aide?: string | undefined
  /** Message d'erreur — celui du serveur ou du schéma Zod partagé. */
  readonly erreur?: string | undefined
}

/**
 * Champ de saisie avec libellé associé.
 *
 * Le libellé est un vrai `<label>`, jamais un `placeholder` : un placeholder
 * disparaît dès la première frappe, et l'utilisateur qui hésite ne sait plus
 * ce qu'on lui demande (skill `frontend-react-afs` § Accessibilité).
 *
 * Un champ `type="password"` reçoit d'office sa bascule d'affichage — voir
 * plus bas pourquoi elle n'est pas une option qu'on pense à activer.
 */
export const Champ = forwardRef<HTMLInputElement, ProprietesChamp>(function Champ(
  { libelle, aide, erreur, required, type, ...reste },
  ref,
) {
  const id = useId()
  const idAide = `${id}-aide`
  const idErreur = `${id}-erreur`

  /*
   * La bascule n'est pas une propriété à passer champ par champ : on
   * l'oublierait exactement là où elle sert, et un mot de passe masqué dans
   * un écran et affichable dans un autre est une incohérence que
   * l'utilisateur lit comme un défaut.
   *
   * Elle est masquée par défaut, et le reste à chaque montage : afficher est
   * un geste délibéré, jamais un état hérité de l'écran précédent. Un
   * comptoir d'accueil est un lieu de passage.
   */
  const estMotDePasse = type === 'password'
  const [revele, setRevele] = useState(false)

  const decrit = [aide ? idAide : '', erreur ? idErreur : ''].filter(Boolean).join(' ')

  return (
    <div className={styles.groupe}>
      <label htmlFor={id} className={`${styles.libelle} ${required ? styles.requis : ''}`}>
        {libelle}
      </label>

      <div className={styles.zoneSaisie}>
        <input
          {...reste}
          ref={ref}
          id={id}
          type={estMotDePasse && revele ? 'text' : type}
          required={required}
          aria-required={required === true ? true : undefined}
          aria-invalid={erreur ? true : undefined}
          aria-describedby={decrit || undefined}
          className={`${styles.saisie} ${estMotDePasse ? styles.avecBascule : ''} ${
            erreur ? styles.enErreur : ''
          }`}
        />

        {estMotDePasse && (
          <button
            type="button"
            className={styles.bascule}
            /*
             * `type="button"` : sans lui, un bouton dans un formulaire le
             * soumet. Afficher son mot de passe déclencherait la connexion,
             * avec une saisie souvent incomplète — et une tentative
             * consommée sur les cinq avant verrouillage.
             */
            disabled={reste.disabled}
            aria-pressed={revele}
            aria-controls={id}
            /* Le nom accessible contient le texte visible (WCAG 2.5.3) :
               la commande vocale « Afficher » désigne bien ce bouton. */
            aria-label={revele ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
            onClick={() => {
              setRevele((etat) => !etat)
            }}
          >
            {revele ? 'Masquer' : 'Afficher'}
          </button>
        )}
      </div>

      {aide && !erreur && (
        <span id={idAide} className={styles.aide}>
          {aide}
        </span>
      )}

      {erreur && (
        <span id={idErreur} className={styles.erreur} role="alert">
          {erreur}
        </span>
      )}
    </div>
  )
})
